import styled from '@emotion/styled';
import type { SetRequired } from 'type-fest';

import { createClassNames } from '@hrworks/design-system/functions';
import { lineClamp, overflowEllipsis, overflowHyphens, shouldForwardProp } from '@hrworks/design-system/utilities';
import type { Typography } from '@hrworks/sui-shared/types';

import type { TitleProps as _TitleProps } from './Title.types';

type TitleContainerProps = SetRequired<
  Pick<_TitleProps, 'alignTitle' | 'overflowBehaviour'> & { typography?: Typography },
  'typography'
>;

const TitleContainer = styled.div<TitleContainerProps>(({ theme, typography, alignTitle, overflowBehaviour }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5em',
  justifyContent: alignTitle,

  ...theme.eclipseTier2Typography[typography],

  ...(overflowBehaviour === 'ellipsis' && {
    overflow: 'hidden',
  }),
}));

type TitleProps = {
  $overflowBehaviour: NonNullable<_TitleProps['overflowBehaviour']>;
  $uppercase?: _TitleProps['uppercase'];
  $alignTitle?: _TitleProps['alignTitle'];
  $lineClamp?: _TitleProps['lineClamp'];
};

const Title = styled('div', {
  shouldForwardProp: (prop) => prop !== 'as' && shouldForwardProp(prop),
})<TitleProps>(({ $uppercase, $overflowBehaviour, $alignTitle, $lineClamp }) => [
  overflowHyphens,
  { textWrap: 'balance' },
  $overflowBehaviour === 'ellipsis' && overflowEllipsis,
  $lineClamp && lineClamp($lineClamp),
  {
    fontWeight: 'inherit',
    fontSize: 'inherit',
    textAlign: $alignTitle,
    ...($uppercase && {
      textTransform: 'uppercase',
    }),
    margin: 0,
  },
]);

const PaddedChildren = styled.span(({ theme }) => ({
  paddingRight: theme.eclipseTier1.spacing['8px'],
}));

const TitleChildren = styled.div(({ theme }) => ({
  display: 'inline-flex',
  gap: theme.eclipseTier1.spacing['8px'],
  pointerEvents: 'auto',
}));

const components = { TitleContainer, Title, PaddedChildren, TitleChildren };

export const S = {
  ...components,
  ...createClassNames('Title', components),
} as const;
